import React, { ReactNode } from 'react';
import { useTranslation } from 'next-i18next';
import BaseLayout from '~/components/pay-fine/common-component/layout';
import StepIndicator, { StepBackButton } from '~/components/common/step-indicator';
import WithTimeout from '~/components/common/withTimeout';
import PageTitle from '~/components/common/page-title';
import { styled } from 'styled-components';
import { spacing } from '~/theme';
import { useAuthentication } from '~/components/address-change/hook';
import { HRuler } from '~/theme/elements';
import { urlWithBasePath } from '~/utils/url-helpers';
import { useRouter } from 'next/router';
import Spinner from '~/components/common/spinner';
import { defaultLocale } from '~/locale/defaults';
import useCheckMaintenance from '~/hooks/check-maintenance';
import Maintenance from 'pages/maintenance';
import { usePathname } from 'next/navigation';
import { useProfileTier } from '~/hooks/useProfileTier';
import { PROFILE_TIERS } from '~/constants/profileTier';
import { useStateValue } from '~/store/store';

export interface StepInfo {
    currentStep: number;
    totalSteps: number;
    prevUrl?: string;
    isPayfee?: boolean;
    onBackClick?: () => void;
}

interface Layout {
    stepInfo?: StepInfo;
    children: ReactNode;
    showCreateAccount?: boolean;
    pageTitle?: string;
}

const ContainerSet = styled.div`
    padding-top: ${spacing.spacing3};
    padding-bottom: ${spacing.spacing3};
    gap: ${spacing.spacing4};
    display: flex;
`;
const ROUTES = {
    DASHBOARD: '/dashboard',
    HOMEPAGE: '/pay-fine',
    ORDER: '/pay-fine/order',
    DLDASHBOARD: '/products/drivers-licence',
};
const Layout = ({ stepInfo, children, showCreateAccount = true, pageTitle }: Layout) => {
    const { t } = useTranslation('pay-fine');
    const { isGuestUser } = useAuthentication();
    const { state } = useStateValue();
    const { loading: checkMaintenanceLoading, message: maintenanaceMessage } = useCheckMaintenance({
        app: 'PAYFINE',
    });

    const router = useRouter();
    const BACK_PAGE_URL = urlWithBasePath(`${router.locale ?? defaultLocale}${ROUTES.DASHBOARD}`);
    const HOME_PAGE_URL = urlWithBasePath(`${router.locale ?? defaultLocale}${ROUTES.HOMEPAGE}`);
    const DLDASHBOARD_URL = urlWithBasePath(
        `${router.locale ?? defaultLocale}${ROUTES.DLDASHBOARD}`
    );
    const pathname = usePathname();
    const { profileTier } = useProfileTier();

    if (checkMaintenanceLoading) {
        return <Spinner isLoading />;
    }

    if (maintenanaceMessage?.isActive) {
        return <Maintenance message={maintenanaceMessage} />;
    }
    const titleOfThePage = pageTitle ?? t('PageTitle');
    return (
        <>
            <PageTitle title={titleOfThePage} />
            <BaseLayout
                customAppTitle={titleOfThePage ?? ''}
                showCreateAccountBanner={showCreateAccount}
            >
                {stepInfo && stepInfo.currentStep !== 0 && (
                    <StepIndicator
                        currentStep={stepInfo?.currentStep}
                        totalSteps={stepInfo?.totalSteps}
                        prevUrl={stepInfo?.prevUrl}
                        onBackClick={stepInfo.onBackClick}
                    />
                )}
                {stepInfo?.currentStep === 0 && (
                    <>
                        <ContainerSet>
                            {isGuestUser ? (
                                <StepBackButton
                                    label={t('backText')}
                                    href={
                                        stepInfo?.isPayfee
                                            ? `${t('common:ontarioCaUrl')}`
                                            : HOME_PAGE_URL
                                    }
                                />
                            ) : profileTier === PROFILE_TIERS.T2 &&
                              (pathname === '/pay-fine/no-fines' ||
                                  pathname === '/pay-fine/person-visit') ? (
                                state?.payFineInitBy === 'DLDashboard' ? (
                                    <StepBackButton label={t('backText')} href={DLDASHBOARD_URL} />
                                ) : (
                                    <StepBackButton label={t('backText')} href={BACK_PAGE_URL} />
                                )
                            ) : profileTier === PROFILE_TIERS.T2 ? (
                                <StepBackButton
                                    label={t('backText')}
                                    href="#"
                                    onClick={() => {
                                        if (stepInfo?.onBackClick) {
                                            stepInfo.onBackClick();
                                            return;
                                        }

                                        router.back();
                                    }}
                                />
                            ) : (
                                <StepBackButton
                                    label={t('pay-fine:order-complete.backToHome')}
                                    href={BACK_PAGE_URL}
                                />
                            )}
                        </ContainerSet>
                        <HRuler />
                    </>
                )}

                {children}
            </BaseLayout>
        </>
    );
};
export default function PayFineLayout(props: Layout) {
    return (
        <WithTimeout requireAuthentication={false}>
            <Layout {...props} />
        </WithTimeout>
    );
}
